if (!window.__shoespray_completeWithLoaded) {
  window.__shoespray_completeWithLoaded = true;

  class CompleteWithSectionHandler extends HTMLElement {
    constructor() {
      super();
      this.viewModel = null;
    }

    connectedCallback() {
      this.viewModel = this.getViewModel();
      this.initialize();
    }

    getViewModel() {
      return {
        $completeWithSubmit: document.querySelector('[data-replaceable-complete-with-submit]'),
        $modal: document.getElementById('complete-with-modal-color-product'),
        $card: this.querySelector('[data-replaceable-complete-with-card]'),
      };
    }

    initialize() {
      const { $completeWithSubmit, $modal, $card } = this.viewModel;

      $card.addEventListener('click', (event) => {
        if (this.cardImage?.contains(event.target)) {
          return;
        }

        if (this.sizeTrigger?.contains(event.target)) {
          this.handleSizeTrigger();
          return;
        }

        if (this.colorTrigger?.contains(event.target)) {
          this.handleColorTrigger();
          return;
        }

        if (!this.isSizeSelected && this.sizeTrigger) {
          event.preventDefault();
          event.stopImmediatePropagation();
          // When there is a size selection required open size modal view
          this.handleSizeTrigger();
          $modal?.open();
        } else {
          this.handleCheckboxChange(this.completeWithCheckbox.checked);
        }
      });

      $completeWithSubmit?.addEventListener('click', () => {
        if (!this.isSizeSelected && this.sizeTrigger) {
          this.sizeVariants.classList.add('is-error');
          this.setButtonText(window.variantStrings.select_size);
          return;
        }

        const checkbox = this.completeWithCheckbox;
        checkbox.checked = true;
        this.handleCheckboxChange(true);
        $modal?.close();
      });

      this.addEventListener('variant:updated', (e) => this.onVariantUpdated(e.detail));
    }

    handleSizeTrigger() {
      const { $modal } = this.viewModel;
      $modal?.classList.add('size-modal');
      $modal?.classList.remove('color-modal');
    }

    handleColorTrigger() {
      const { $modal } = this.viewModel;
      $modal?.classList.add('color-modal');
      $modal?.classList.remove('size-modal');
    }

    onVariantUpdated({ variant, size }) {
      if (this.isSizeSelected) {
        this.setButtonAvailable();
        this.sizeVariants.classList.remove('is-error');
      }

      if (variant && (!variant.available || variant.inventory_quantity === 0)) {
        this.setButtonOutOfStock();
        return;
      }

      this.setSelectedVariant(variant);
      this.setSizeLabel(size);
    }

    setSelectedVariant(variant) {
      if (variant?.id) {
        this.completeWithCheckbox.value = variant.id;
      }
    }

    setSizeLabel(size) {
      this.sizeTrigger.classList.add('selected');
      this.sizeTrigger.querySelector('.input-chip__leading-label').textContent =
        `${window.product.size_label} ${size.reverse().join(', ')}`;
    }

    handleCheckboxChange(isChecked) {
      if (isChecked) {
        this.submitButtonComponent.setDefaultTextValue();
      } else {
        this.submitButtonComponent.setDefaultTextValue();
      }
    }

    setButtonText(text) {
      this.completeWithSubmitLabel.textContent = text;
    }

    setButtonOutOfStock() {
      this.setButtonText(window.variantStrings.outOfStock || 'Out of Stock');
      this.completeWithSubmit.setAttribute('disabled', 'true');
    }

    setButtonAvailable() {
      this.setButtonText(window.variantStrings.done || 'Done');
      this.completeWithSubmit.removeAttribute('disabled');
    }

    get completeWithSubmit() {
      return document.querySelector('[data-replaceable-complete-with-submit] [data-complete-with-done]');
    }

    get completeWithSubmitLabel() {
      return this.completeWithSubmit.querySelector('.shared-button__leading-label');
    }

    get submitButtonComponent() {
      return document.querySelector('add-to-cart-button');
    }

    get isSizeSelected() {
      return this.sizeVariants?.getAttribute('data-size-selected') === 'true';
    }

    get sizeVariants() {
      return document.querySelector('[data-replaceable-complete-with-variants] .variant-selects');
    }

    get completeWithCheckbox() {
      return this.querySelector('input[type="checkbox"]');
    }

    get sizeTrigger() {
      return this.querySelector('.complete-with__size-trigger');
    }

    get cardImage() {
      return this.querySelector('.complete-with__image');
    }

    get colorTrigger() {
      return this.querySelector('.complete-with__color-trigger');
    }
  }

  customElements.define('complete-with-section', CompleteWithSectionHandler);

  class VariantBottomSheet extends HTMLElement {
    constructor() {
      super();
      this.viewModel = null;
      this.variants = [];
      this.currentSize = null;
      this._onInputChange = this._onInputChange.bind(this);
      this._onGridClick = this._onGridClick.bind(this);
    }

    connectedCallback() {
      this.viewModel = this.getViewModel();
      const json = this.viewModel.$script?.textContent?.trim();
      this.variants = window.utilities.safeParseJSON(json, []);
      this.initialize();
    }

    disconnectedCallback() {
      this.viewModel?.$inputs?.forEach((input) => {
        input.removeEventListener('change', this._onInputChange);
      });
      this.removeEventListener('click', this._onGridClick, true);
    }

    getViewModel() {
      return {
        $inputs: [...this.querySelectorAll('input.grid-selector__input')],
        $script: this.querySelector('script[data-variants-bottom-sheet]'),
        $modal: this.closest('modal-slideout'),
      };
    }

    initialize() {
      this.removeEventListener('click', this._onGridClick, true);
      this.addEventListener('click', this._onGridClick, true);

      this.viewModel.$inputs.forEach((input) => {
        input.removeEventListener('change', this._onInputChange);
        input.addEventListener('change', this._onInputChange);
      });

      this._maybeUnselectSizeInputs();
    }

    refreshBindings() {
      this.initialize();
    }

    /**
     * Select radios within this picker only (avoids duplicate id/name collisions in document).
     * @param {HTMLInputElement} input
     */
    _selectInputInComponent(input) {
      if (!input?.name) return;

      this.querySelectorAll(`input.grid-selector__input[name="${CSS.escape(input.name)}"]`).forEach((radio) => {
        const isSelected = radio === input;
        radio.checked = isSelected;
        radio.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        if (isSelected) {
          radio.setAttribute('checked', 'checked');
        } else {
          radio.removeAttribute('checked');
        }
      });
    }

    _onGridClick(event) {
      const wrapper = event.target.closest('.grid-selector__wrapper');
      if (!wrapper || !this.contains(wrapper)) return;

      const input = wrapper.querySelector('input.grid-selector__input');
      if (!input || input.disabled) return;

      event.preventDefault();
      this._selectInputInComponent(input);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _onInputChange(event) {
      const target = event.target;
      this._handleSizeSelection(target);

      this.updateOptionAvailability();

      const variant = this.getResolvedVariant();

      this._handleLowStockMessaging(variant);
      this.reconcileSizeSelectionUI();

      if (this.connectedCard) {
        this.connectedCard.dispatchEvent(
          new CustomEvent('variant:updated', {
            detail: { variant, size: this.getSelectedValues() },
            bubbles: true,
          }),
        );
      }
    }

    reconcileSizeSelectionUI() {
      const sizeFieldset = this.querySelector('[data-size-option-fieldset]');
      if (!sizeFieldset) return;

      sizeFieldset.querySelectorAll('.grid-selector__label--inner-container-selected').forEach((element) => {
        element.classList.remove('grid-selector__label--inner-container-selected');
      });

      const checked = sizeFieldset.querySelector('input[type="radio"]:checked');
      if (!checked || checked.disabled) return;

      checked
        .closest('.grid-selector__wrapper')
        ?.querySelector('.grid-selector__label--inner-container')
        ?.classList.add('grid-selector__label--inner-container-selected');

      const label = sizeFieldset.querySelector('.product-selector-label-current-selection');
      const sizeValueEl = label?.querySelector('[data-size-value]');
      if (sizeValueEl) {
        sizeValueEl.textContent = checked.value;
      } else if (label && window.utilities?.replaceTextOnly) {
        window.utilities.replaceTextOnly(label, ` ${checked.value}`);
      }
    }

    updateOptionAvailability() {
      // Don't change anything until size is selected
      if (!this.isSizeSelected) return;

      const selectedValues = this.getSelectedValues();
      const optionCount = selectedValues.length;

      this.viewModel.$inputs.forEach((input) => {
        input.disabled = false;
        input.classList.remove('is-unavailable');
      });

      const inputsByOption = this.groupInputsByOptionIndex();

      inputsByOption.forEach((inputsInGroup, optionIndex) => {
        inputsInGroup.forEach((input) => {
          const testValue = input.value;
          const matchValues = [...selectedValues];
          matchValues[optionIndex] = testValue;

          const matchingVariant = this.variants.find((variant) => {
            return variant.options.length === optionCount && variant.options.every((val, i) => matchValues[i] === val);
          });

          if (!matchingVariant || !matchingVariant.available || matchingVariant.inventory_quantity === 0) {
            input.classList.add('is-unavailable');
          }
        });
      });
    }

    getSelectedValues() {
      return this.groupInputsByOptionIndex()
        .map((group) => group.find((input) => input.checked)?.value)
        .filter((value) => value != null && value !== '');
    }

    getResolvedVariant() {
      const selectedValues = this.getSelectedValues();
      if (selectedValues.length === 0) return null;

      return this.variants.find(
        (v) => v.options.length === selectedValues.length && v.options.every((val, i) => val === selectedValues[i]),
      );
    }

    groupInputsByOptionIndex() {
      const groups = [];
      this.viewModel.$inputs.forEach((input) => {
        const index = window.utilities.toNumber(input?.dataset?.optionPosition) ?? 0;

        if (!groups[index]) groups[index] = [];
        groups[index].push(input);
      });
      return groups;
    }

    _handleLowStockMessaging(variant) {
      const $lowStockMessage = this.closest('modal-slideout')?.querySelector('.product-low-stock-messaging');
      const $lowStockMessageText = $lowStockMessage?.querySelector('.low-inventory-message--text');
      if (!$lowStockMessage || !$lowStockMessageText || !variant) return;

      const lowStockThreshold = window?.product?.lowStockValue ?? 0;

      if (variant.inventory_quantity <= 0 && variant.available == false) {
        const sizeOptionValue = variant.options?.at(-1);
        const oosTranslation = window.product.out_of_stock_variant?.replace('{{ size_value }}', sizeOptionValue) ?? '';
        $lowStockMessageText.textContent = oosTranslation;
        $lowStockMessage.classList.remove('hidden');
      } else if (variant.inventory_quantity < lowStockThreshold) {
        const lowStockTranslation = window.product.lowstock.replace('{{ quantity }}', lowStockThreshold) ?? '';
        $lowStockMessageText.textContent = lowStockTranslation;
        $lowStockMessage.classList.remove('hidden');
      } else {
        $lowStockMessageText.textContent = '';
        $lowStockMessage.classList.add('hidden');
      }
    }

    _handleSizeSelection(target) {
      if (!target || !target.name) return;

      const $currentSelectionLabel = target
        .closest('.product-form__input')
        ?.querySelector('.product-selector-label-current-selection');
      if ($currentSelectionLabel) {
        window.utilities.replaceTextOnly($currentSelectionLabel, ' ' + target.value);
      }

      const isSizeOption = target.closest('[data-size-option-fieldset]');

      // Don't set selected size when is unavailable
      if (isSizeOption && !target.classList.contains('is-unavailable')) {
        this.currentSize = target.value;
        this.setAttribute('data-size-selected', 'true');
      }
    }

    _maybeUnselectSizeInputs() {
      const input = this.querySelector('[data-size-option-fieldset] input[type="radio"]:checked');
      if (!input) return;

      input.checked = false;
      input.removeAttribute('checked');
      input.setAttribute('aria-checked', 'false');
    }

    get isSizeSelected() {
      return this.getAttribute('data-size-selected') === 'true';
    }

    get connectedCard() {
      return document.querySelector(`[data-id-buy-stack-connector="${this.viewModel.$modal.id}"]`);
    }
  }

  customElements.define('variants-bottom-sheet', VariantBottomSheet);

  class VariantBottomSheetCart extends VariantBottomSheet {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      this.selectVariantById();
    }
    // Overridden: intentionally left blank
    _maybeUnselectSizeInputs() {}

    selectVariantById() {
      const id = this.viewModel.$modal?.id;
      if (!id) return;
      const $triggerElement = document.querySelector(`edit-cart-size[data-modal-id="${id}"]`);
      const variantId = window.utilities.toNumber($triggerElement?.dataset?.variantId);
      const variant = this.variants.find((v) => v.id === variantId);

      if (!variant) return;

      this.viewModel.$inputs.forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
        input.setAttribute('aria-checked', 'false');
      });

      const optionInputs = this.groupInputsByOptionIndex();
      let lastSelectedInput = null;

      variant.options.forEach((optionValue, index) => {
        const group = optionInputs[index];
        if (!group) return;

        const inputToSelect = group.find((input) => input.value === optionValue);

        if (inputToSelect) {
          this._selectInputInComponent(inputToSelect);
          lastSelectedInput = inputToSelect;
        }
      });

      // Dispatch only once — for the last input selected
      if (lastSelectedInput) {
        lastSelectedInput.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        this.reconcileSizeSelectionUI();
      }
    }
  }

  customElements.define('variants-bottom-sheet-cart', VariantBottomSheetCart);

  const EDIT_CART_WIRE_MAX_RAF_ATTEMPTS = 30;
  const EDIT_CART_WIRE_OBSERVER_MS = 5000;

  /**
   * Retries edit-cart wiring until the portaled modal-slideout is in the DOM.
   * @param {EditCartSize | EditCartColor} element
   * @param {() => boolean} tryWire
   */
  function scheduleEditCartWire(element, tryWire) {
    let attempts = 0;
    let rafId = null;
    let observer = null;
    let timeoutId = null;
    const modalId = element.dataset.modalId;

    const cancel = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      observer?.disconnect();
      observer = null;
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (element._cancelEditCartWire === cancel) {
        element._cancelEditCartWire = null;
      }
    };

    const attempt = () => {
      if (!element.isConnected) {
        cancel();
        return;
      }
      if (tryWire()) {
        cancel();
        return;
      }
      attempts += 1;
      if (attempts < EDIT_CART_WIRE_MAX_RAF_ATTEMPTS) {
        rafId = requestAnimationFrame(attempt);
        return;
      }
      if (!observer && modalId) {
        observer = new MutationObserver(() => {
          if (tryWire()) cancel();
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        timeoutId = setTimeout(cancel, EDIT_CART_WIRE_OBSERVER_MS);
      }
    };

    element._cancelEditCartWire?.();
    element._cancelEditCartWire = cancel;
    queueMicrotask(attempt);
  }

  /**
   * Ensures edit-cart wiring runs when the customer opens the size/color chip.
   * @param {EditCartSize | EditCartColor} element
   * @param {() => boolean} tryWire
   */
  function bindEditCartTriggerWire(element, tryWire) {
    const trigger = element.querySelector('button');
    if (!trigger || element._editCartTriggerWireBound) return;

    element._editCartTriggerWireBound = true;
    element._onTriggerWire = () => {
      if (!element._wired) tryWire();
    };
    trigger.addEventListener('click', element._onTriggerWire, { capture: true });
  }

  /**
   * @param {string | number | null | undefined} id
   * @returns {string | null}
   */
  function normalizeVariantId(id) {
    if (id == null || id === '') return null;
    return String(id);
  }

  /**
   * Delegates cart line replacement so edit-cart commits survive cart-drawer re-renders.
   */
  function bindEditCartForceRefreshOnce() {
    if (window.__editCartForceRefreshBound) return;
    window.__editCartForceRefreshBound = true;

    document.addEventListener('cart:force-refresh', async (event) => {
      const detail = event.detail;
      if (!detail) return;

      const openDrawer = document.querySelector('cart-drawer[open]');
      const cartItems =
        openDrawer?.querySelector('cart-drawer-items') ||
        document.querySelector('cart-drawer-items') ||
        document.querySelector('cart-items');

      if (!cartItems || typeof cartItems.itemReplace !== 'function') return;

      try {
        await cartItems.itemReplace(detail);
      } catch (err) {
        console.error('Error in itemReplace:', err);
      }
    });
  }

  bindEditCartForceRefreshOnce();

  /**
   * Routes Done clicks through the paired edit-cart element (survives modal re-portaling).
   */
  function bindEditCartDoneDelegationOnce() {
    if (window.__editCartDoneDelegationBound) return;
    window.__editCartDoneDelegationBound = true;

    document.addEventListener('click', (event) => {
      const sizeDone = event.target.closest('[data-cart-edit-size-done]');
      if (sizeDone) {
        const modal = sizeDone.closest('modal-slideout');
        const editCart = modal?.id ? document.querySelector(`edit-cart-size[data-modal-id="${modal.id}"]`) : null;
        if (editCart?._onClick) {
          event.preventDefault();
          editCart._onClick();
        }
        return;
      }

      const colorDone = event.target.closest('[data-cart-edit-color-done]');
      if (!colorDone) return;

      const modal = colorDone.closest('modal-slideout');
      const editCart = modal?.id ? document.querySelector(`edit-cart-color[data-modal-id="${modal.id}"]`) : null;
      if (editCart?._onClick) {
        event.preventDefault();
        editCart._onClick();
      }
    });
  }

  bindEditCartDoneDelegationOnce();

  /**
   * Re-run edit-cart wiring for elements inside a freshly rendered cart drawer.
   * @param {ParentNode} [root]
   */
  window.wireEditCartElements = (root = document) => {
    root.querySelectorAll('edit-cart-size, edit-cart-color').forEach((element) => {
      element._unwire?.();
      const tryWire = () => element._tryWire?.() ?? false;
      scheduleEditCartWire(element, tryWire);
      bindEditCartTriggerWire(element, tryWire);
    });
  };

  class EditCartSize extends HTMLElement {
    constructor() {
      super();
      this.viewModel = { $bottomSheetModal: null, $detailsHolder: null, $triggerButton: null };
      this.variant = this.dataset.variantId;
      this.selectedVariant = null;

      // Bindings for cleanup
      this._onClick = this._onClick.bind(this);
      this._onVariantUpdated = this._onVariantUpdated.bind(this);
    }

    connectedCallback() {
      const tryWire = () => this._tryWire();
      scheduleEditCartWire(this, tryWire);
      bindEditCartTriggerWire(this, tryWire);
    }

    _tryWire() {
      if (!this.isConnected || this._wired) return true;

      this.viewModel = this.getViewModel();
      if (!this.viewModel.$bottomSheetModal) return false;

      this._wired = true;
      this.syncVariantFromDataset();
      this.patchModalLifecycle();
      this.addEventListener('variant:updated', this._onVariantUpdated);
      return true;
    }

    _unwire() {
      this._cancelEditCartWire?.();
      this._wired = false;

      const trigger = this.querySelector('button');
      if (trigger && this._onTriggerWire) {
        trigger.removeEventListener('click', this._onTriggerWire, { capture: true });
      }
      this._onTriggerWire = null;
      this._editCartTriggerWireBound = false;

      const modal = this._resolveModal();
      if (modal && this._originalModalOpen) {
        modal.open = this._originalModalOpen;
      }
      this._originalModalOpen = null;

      this.removeEventListener('variant:updated', this._onVariantUpdated);
    }

    _resolveModal() {
      return document.getElementById(this.dataset.modalId) || this.viewModel?.$bottomSheetModal || null;
    }

    _getSelectedVariantId() {
      const modal = this._resolveModal();
      const bottomSheet = modal?.querySelector('variants-bottom-sheet-cart');
      const variant = bottomSheet?.getResolvedVariant?.();
      if (variant?.id != null) return variant.id;

      return this.selectedVariant;
    }

    patchModalLifecycle() {
      const modal = this._resolveModal();
      if (!modal || typeof modal.open !== 'function' || this._originalModalOpen) return;

      this._originalModalOpen = modal.open;

      modal.open = (...args) => {
        this.viewModel = this.getViewModel();
        if (!this._wired) this._tryWire();

        const bottomSheet = modal.querySelector('variants-bottom-sheet-cart');
        bottomSheet?.refreshBindings?.();
        bottomSheet?.selectVariantById?.();

        this.syncVariantFromDataset();
        this.selectedVariant = this._getSelectedVariantId();
        this.setButtonAvailable();

        return this._originalModalOpen.apply(modal, args);
      };
    }

    // we need this so attributeChangedCallback is called
    static get observedAttributes() {
      return ['disabled'];
    }

    attributeChangedCallback(name) {
      if (name === 'disabled') {
        this.querySelector('button')?.setAttribute('disabled', 'true');
      }
    }

    _onClick() {
      if (this.editCartSizeSubmit?.classList.contains('is-loading')) return;

      const $bottomSheetModal = this._resolveModal();
      const originalVariantId = normalizeVariantId(this.dataset.variantId ?? this.variant);
      const newVariantId = normalizeVariantId(this._getSelectedVariantId());

      if (newVariantId && newVariantId !== originalVariantId) {
        this.editCartSizeSubmit?.classList.add('is-loading');

        document.dispatchEvent(
          new CustomEvent('cart:force-refresh', {
            detail: {
              lineIndex: this.dataset.lineIndex,
              originalVariantId: this.variant,
              newVariantId,
              quantity: this.dataset.qty,
              bottomSheetModal: $bottomSheetModal,
            },
          }),
        );
      } else {
        $bottomSheetModal?.close?.();
      }
    }

    _onVariantUpdated(e) {
      const { variant } = e.detail;

      if (variant && (!variant.available || variant.inventory_quantity === 0)) {
        this.setButtonOutOfStock();
      } else {
        this.setButtonAvailable();
        this.selectedVariant = variant.id;
      }

      if (variant) {
        window.utilities?.updateCartLineLowStock?.(this.closest('.cart-item__holder'), variant);
      }
    }

    disconnectedCallback() {
      const trigger = this.querySelector('button');
      if (trigger && this._onTriggerWire) {
        trigger.removeEventListener('click', this._onTriggerWire, { capture: true });
      }
      this._onTriggerWire = null;
      this._editCartTriggerWireBound = false;
      this._unwire();
    }

    syncVariantFromDataset() {
      this.variant = this.dataset.variantId;
    }

    setButtonText(text) {
      this.editCartSizeSubmitLabel.textContent = text;
    }

    setButtonOutOfStock() {
      this.setButtonText(window.variantStrings.outOfStock || 'Out of Stock');
      this.editCartSizeSubmit?.setAttribute('disabled', 'true');
    }

    setButtonAvailable() {
      this.setButtonText(window.variantStrings.done || 'Done');
      this.editCartSizeSubmit?.removeAttribute('disabled');
    }

    getViewModel() {
      return {
        $bottomSheetModal: document.getElementById(this.dataset.modalId),
        $detailsHolder: this.closest('.cart-item__details-holder'),
        $triggerButton: this.querySelector('button'),
      };
    }

    get editCartSizeSubmit() {
      return this._resolveModal()?.querySelector('[data-cart-edit-size-done]');
    }

    get editCartSizeSubmitLabel() {
      return this.editCartSizeSubmit?.querySelector('.shared-button__leading-label');
    }
  }

  customElements.define('edit-cart-size', EditCartSize);

  class EditCartColor extends HTMLElement {
    constructor() {
      super();
      this.viewModel = { $bottomSheetModal: null, $triggerButton: null };
      this.variant = this.dataset.variantId;
      this.selectedVariant = null;
      this._originalSwatchInput = null;
      this._originalModalClose = null;
      this._originalModalOpen = null;
      this._skipRevertOnClose = false;

      // Bindings for cleanup
      this._onClick = this._onClick.bind(this);
      this._onVariantUpdated = this._onVariantUpdated.bind(this);
      this._swatchHandlers = [];
    }

    connectedCallback() {
      const tryWire = () => this._tryWire();
      scheduleEditCartWire(this, tryWire);
      bindEditCartTriggerWire(this, tryWire);
    }

    _tryWire() {
      if (!this.isConnected || this._wired) return true;

      this.viewModel = this.getViewModel();
      if (!this.viewModel.$bottomSheetModal) return false;

      this._wired = true;
      this.variant = this.dataset.variantId;

      this._bindSwatchHandlers();
      this.patchModalLifecycle();

      return true;
    }

    _unwire() {
      this._cancelEditCartWire?.();
      this._swatchHandlers.forEach(({ swatch, handler }) => {
        swatch.removeEventListener('click', handler);
      });
      this._swatchHandlers = [];
      this._wired = false;

      const trigger = this.querySelector('button');
      if (trigger && this._onTriggerWire) {
        trigger.removeEventListener('click', this._onTriggerWire, { capture: true });
      }
      this._onTriggerWire = null;
      this._editCartTriggerWireBound = false;

      const modal = this._resolveModal();
      if (modal) {
        if (this._originalModalClose) modal.close = this._originalModalClose;
        if (this._originalModalOpen) modal.open = this._originalModalOpen;
      }
      this._originalModalClose = null;
      this._originalModalOpen = null;

    }

    _resolveModal() {
      return document.getElementById(this.dataset.modalId) || this.viewModel?.$bottomSheetModal || null;
    }

    _getSelectedVariantId() {
      const modal = this._resolveModal();
      const checked = modal?.querySelector('input.color-selector__radio:checked');
      const fromDom = checked?.getAttribute('data-variant-id');
      if (fromDom) return fromDom;

      return this.selectedVariant;
    }

    _bindSwatchHandlers() {
      this._swatchHandlers.forEach(({ swatch, handler }) => {
        swatch.removeEventListener('click', handler);
      });
      this._swatchHandlers = [];

      const modal = this._resolveModal();
      if (!modal) return;

      modal.querySelectorAll('.color-selector').forEach((swatch) => {
        const handler = () => this._onVariantUpdated(swatch);
        swatch.addEventListener('click', handler);
        this._swatchHandlers.push({ swatch, handler });
      });
    }

    // we need this so attributeChangedCallback is called
    static get observedAttributes() {
      return ['disabled'];
    }

    attributeChangedCallback(name) {
      if (name === 'disabled') {
        this.querySelector('button')?.setAttribute('disabled', 'true');
      }
    }

    _onClick() {
      const $bottomSheetModal = this._resolveModal();
      const originalVariantId = normalizeVariantId(this.dataset.variantId ?? this.variant);
      const newVariantId = normalizeVariantId(this._getSelectedVariantId());

      if (newVariantId && newVariantId !== originalVariantId) {
        this._skipRevertOnClose = true;
        this.editCartColorSubmit?.classList.add('is-loading');

        document.dispatchEvent(
          new CustomEvent('cart:force-refresh', {
            detail: {
              lineIndex: this.dataset.lineIndex,
              originalVariantId: this.variant,
              newVariantId,
              quantity: this.dataset.qty,
              bottomSheetModal: $bottomSheetModal,
            },
          }),
        );
      } else {
        $bottomSheetModal?.close?.();
      }
    }

    _onVariantUpdated(swatch) {
      // data-variant-id is set by EditCartMVPHandler on the radio input inside the label
      const variantId =
        swatch.querySelector?.('input[data-variant-id]')?.getAttribute('data-variant-id') ??
        swatch.getAttribute('data-variant-id');
      this.selectedVariant = variantId;
    }

    disconnectedCallback() {
      const trigger = this.querySelector('button');
      if (trigger && this._onTriggerWire) {
        trigger.removeEventListener('click', this._onTriggerWire, { capture: true });
      }
      this._onTriggerWire = null;
      this._editCartTriggerWireBound = false;
      this._unwire();
    }

    patchModalLifecycle() {
      const modal = this._resolveModal();
      if (!modal || typeof modal.close !== 'function' || typeof modal.open !== 'function') return;

      if (this._originalModalClose) return;

      this._originalModalClose = modal.close;
      this._originalModalOpen = modal.open;

      modal.open = (...args) => {
        this.viewModel = this.getViewModel();
        if (!this._wired) this._tryWire();
        this.variant = this.dataset.variantId;
        this.selectedVariant = this._getSelectedVariantId();
        this._skipRevertOnClose = false;
        this.captureOriginalSwatch();
        this._bindSwatchHandlers();
        window.syncCheckedColorSwatchStyles?.(modal);

        return this._originalModalOpen.apply(modal, args);
      };

      modal.close = (...args) => {
        if (!this._skipRevertOnClose) {
          this.revertSwatchSelection();
        }
        this._skipRevertOnClose = false;

        return this._originalModalClose.apply(modal, args);
      };
    }

    captureOriginalSwatch() {
      const modal = this._resolveModal();
      if (!modal) return;

      const applySelection = (input) => {
        if (!input) return false;

        const list = input.closest('.color-selector-list');
        if (list) {
          list.querySelectorAll('.color-selector__radio').forEach((radio) => {
            radio.checked = radio === input;
          });
        } else {
          input.checked = true;
        }

        window.syncCheckedColorSwatchStyles?.(modal);
        return true;
      };

      const capture = () => {
        const variantId = String(this.variant);
        const byVariant = modal.querySelector(`input.color-selector__radio[data-variant-id="${variantId}"]`);
        const checked = modal.querySelector('input.color-selector__radio:checked');
        const target = byVariant || checked;

        this._originalSwatchInput = target;
        return applySelection(target);
      };

      if (capture()) return;

      let attempts = 0;
      const retry = () => {
        if (capture() || ++attempts >= 12) return;
        requestAnimationFrame(retry);
      };
      requestAnimationFrame(retry);
    }

    revertSwatchSelection() {
      const originalInput = this._originalSwatchInput;
      const modal = this._resolveModal();
      if (!originalInput || !modal) return;

      const currentChecked = modal.querySelector('input.color-selector__radio:checked');
      if (currentChecked === originalInput) {
        this.selectedVariant = null;
        return;
      }

      originalInput.checked = true;
      originalInput.dispatchEvent(new Event('change', { bubbles: true }));
      window.syncCheckedColorSwatchStyles?.(modal);
      this.selectedVariant = null;
    }

    getViewModel() {
      return {
        $bottomSheetModal: document.getElementById(this.dataset.modalId),
        $triggerButton: this.querySelector('button'),
      };
    }

    get editCartColorSubmit() {
      return this._resolveModal()?.querySelector('[data-cart-edit-color-done]');
    }
  }

  customElements.define('edit-cart-color', EditCartColor);
}
